"use client";

import {
  memo,
  startTransition,
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
} from "react";

import { Input } from "@/components/ui/input";
import { NumberInput } from "@/components/ui/number-field";
import { Textarea } from "@/components/ui/textarea";

export function useDataTableFieldSync<
  Patch extends Record<string, unknown>,
>(onUpdate: (rowId: string, patch: Patch) => void): DataTableFieldSync {
  const onUpdateRef = useRef(onUpdate);
  onUpdateRef.current = onUpdate;

  return useCallback((rowId: string, field: string, value: unknown) => {
    onUpdateRef.current(rowId, { [field]: value } as Patch);
  }, []);
}

export function useDataTableColumnCallbacks<T extends object>(options: T) {
  const ref = useRef(options);
  ref.current = options;
  return ref;
}

export type DataTableFieldSync = (
  rowId: string,
  field: string,
  value: unknown,
) => void;

export const DataTableInputCell = memo(function DataTableInputCell({
  rowId,
  field,
  value,
  onFieldChange,
  className,
  placeholder,
  type = "text",
  ...props
}: {
  rowId: string;
  field: string;
  value: string;
  onFieldChange: DataTableFieldSync;
  className?: string;
  placeholder?: string;
  type?: ComponentProps<typeof Input>["type"];
} & Omit<ComponentProps<typeof Input>, "value" | "onChange" | "type">) {
  const onFieldChangeRef = useRef(onFieldChange);
  onFieldChangeRef.current = onFieldChange;
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [rowId, field, value]);

  return (
    <Input
      type={type}
      className={className}
      placeholder={placeholder}
      value={localValue}
      onChange={(event) => {
        const next = event.target.value;
        setLocalValue(next);
        startTransition(() => {
          onFieldChangeRef.current(rowId, field, next);
        });
      }}
      {...props}
    />
  );
});

export const DataTableNumberCell = memo(function DataTableNumberCell({
  rowId,
  field,
  value,
  onFieldChange,
  className,
  onBlur,
}: {
  rowId: string;
  field: string;
  value: number;
  onFieldChange: DataTableFieldSync;
  className?: string;
  onBlur?: () => void;
}) {
  const onFieldChangeRef = useRef(onFieldChange);
  onFieldChangeRef.current = onFieldChange;
  const [localValue, setLocalValue] = useState(String(value));

  useEffect(() => {
    setLocalValue(String(value));
  }, [rowId, field, value]);

  return (
    <Input
      type="number"
      className={className}
      value={localValue}
      onChange={(event) => {
        const next = event.target.value;
        setLocalValue(next);
        const parsed = next === "" ? 0 : Number(next);
        startTransition(() => {
          onFieldChangeRef.current(
            rowId,
            field,
            Number.isNaN(parsed) ? 0 : parsed,
          );
        });
      }}
      onBlur={onBlur}
    />
  );
});

export const DataTableNumberInputCell = memo(function DataTableNumberInputCell({
  rowId,
  field,
  value,
  onFieldChange,
  className,
  onBlur,
  nullable = false,
  placeholder,
  inputProps,
}: {
  rowId: string;
  field: string;
  value: number | null;
  onFieldChange: DataTableFieldSync;
  className?: string;
  onBlur?: () => void;

  nullable?: boolean;
  placeholder?: string;
  inputProps?: Omit<ComponentProps<typeof NumberInput>, "value" | "onValueChange">;
}) {
  const onFieldChangeRef = useRef(onFieldChange);
  onFieldChangeRef.current = onFieldChange;
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [rowId, field, value]);

  return (
    <NumberInput
      {...inputProps}
      className={className}
      value={localValue}
      placeholder={placeholder}
      onValueChange={(next) => {
        const parsed = next ?? (nullable ? null : 0);
        setLocalValue(parsed);
        startTransition(() => {
          onFieldChangeRef.current(rowId, field, parsed);
        });
      }}
      onBlur={onBlur}
    />
  );
});

export const DataTableTextareaCell = memo(function DataTableTextareaCell({
  rowId,
  field,
  value,
  onFieldChange,
  className,
  rows = 2,
  ...props
}: {
  rowId: string;
  field: string;
  value: string;
  onFieldChange: DataTableFieldSync;
  className?: string;
  rows?: number;
} & Omit<ComponentProps<typeof Textarea>, "value" | "onChange" | "rows">) {
  const onFieldChangeRef = useRef(onFieldChange);
  onFieldChangeRef.current = onFieldChange;
  const [localValue, setLocalValue] = useState(value);

  useEffect(() => {
    setLocalValue(value);
  }, [rowId, field, value]);

  return (
    <Textarea
      rows={rows}
      className={className}
      value={localValue}
      spellCheck={false}
      onChange={(event) => {
        const next = event.target.value;
        setLocalValue(next);
        startTransition(() => {
          onFieldChangeRef.current(rowId, field, next);
        });
      }}
      {...props}
    />
  );
});

export function dataTableListVersion(
  items: Array<{ id: string; name?: string; sortOrder?: number }>,
) {
  return items
    .map((item) => `${item.id}:${item.name ?? ""}:${item.sortOrder ?? ""}`)
    .join("|");
}
